import { useEffect, useState } from 'react';
import { stockApi } from '../../api/resources';
import { dtoVersArticle } from '../../api/mappers';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Une quantité sans décimales inutiles : « 12 », « 2.5 ». */
const qte = (n) => String(Math.round(Number(n || 0) * 1000) / 1000);

/**
 * Le stock du magasin vu par l'employé, ou par le patron en mode terrain :
 * ce qui reste en rayon, le prix de vente à donner au client, et un toucher
 * pour signaler au bureau ce qui manque. Le prix d'achat ne s'y montre pas.
 *
 * @param {object} props propriétés
 * @param {(texte: string) => Promise<boolean>} [props.onSignale] envoie le
 *   message au bureau ; sans lui, pas de bouton
 * @param {(texte: string) => void} [props.setFlash] confirmation à l'écran
 * @returns {import('react').ReactElement} la carte du stock
 */
export function StockMagasin({ onSignale, setFlash }) {
  const [articles, setArticles] = useState(null);
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const [manques, setManques] = useState(false);
  const [signales, setSignales] = useState({});

  useEffect(() => {
    let annule = false;
    stockApi
      .toutes()
      .then((liste) => {
        if (!annule) setArticles((liste || []).map(dtoVersArticle));
      })
      .catch((e) => {
        if (!annule) setErreur(e?.message || 'Le stock n’a pas pu être chargé.');
      });
    return () => {
      annule = true;
    };
  }, []);

  const q = recherche.trim().toLowerCase();
  const visibles = (articles || [])
    .filter((a) => !manques || a.sousSeuil)
    .filter(
      (a) =>
        !q ||
        [a.nom, a.ref, a.cat, a.codeBarre].some((v) =>
          String(v || '')
            .toLowerCase()
            .includes(q),
        ),
    )
    .sort((a, b) => Number(b.sousSeuil) - Number(a.sousSeuil) || a.nom.localeCompare(b.nom));
  const aReappro = (articles || []).filter((a) => a.sousSeuil).length;

  const signale = async (a) => {
    if (!onSignale) return;
    const ok = await onSignale(
      trf('📣 Stock à réapprovisionner : {0} (reste {1} {2})', a.nom, qte(a.stock), a.u || ''),
    );
    if (ok) {
      setSignales((p) => ({ ...p, [a.id]: true }));
      setFlash && setFlash('✓ Signalé au bureau');
    }
  };

  return (
    <Card>
      <Bar
        icon="📦"
        title="Stock du magasin"
        color={C.navy}
        sub={
          articles
            ? trf('{0} articles · {1} à réapprovisionner', articles.length, aReappro)
            : 'Chargement…'
        }
      />
      <div style={{ padding: 13 }}>
        {erreur && <div style={{ fontSize: 10, color: C.red, marginBottom: 8 }}>{erreur}</div>}
        <input
          aria-label="Chercher un article"
          placeholder="🔎 Nom, référence, catégorie ou code-barres"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '9px 10px',
            fontSize: 11,
            marginBottom: 8,
          }}
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 10,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          <input type="checkbox" checked={manques} onChange={(e) => setManques(e.target.checked)} />
          Seulement les articles à réapprovisionner
        </label>
        {articles && visibles.length === 0 && (
          <div style={{ fontSize: 10, color: C.muted, textAlign: 'center', padding: 12 }}>
            Aucun article.
          </div>
        )}
        {visibles.map((a) => (
          <div
            key={a.id}
            data-testid="article-stock"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              borderBottom: '1px solid ' + C.border,
              padding: '8px 0',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800 }}>{a.nom}</div>
              <div style={{ fontSize: 8.5, color: C.muted }}>
                {[a.ref, a.cat].filter(Boolean).join(' · ')}
                {a.pv ? ' · ' + DEVISE + ' ' + fmt(a.pv) : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: a.sousSeuil ? C.red : C.green }}>
                {qte(a.stock)} {a.u || ''}
              </div>
              {a.sousSeuil && (
                <div style={{ fontSize: 8, color: C.red, fontWeight: 700 }}>
                  {trf('Minimum {0}', qte(a.min))}
                </div>
              )}
            </div>
            {onSignale && (
              <button
                type="button"
                disabled={!!signales[a.id]}
                onClick={() => signale(a)}
                style={{
                  border: '1px solid ' + (signales[a.id] ? C.border : C.amber),
                  background: '#fff',
                  color: signales[a.id] ? C.muted : C.amber,
                  borderRadius: 8,
                  fontSize: 9,
                  fontWeight: 800,
                  padding: '6px 8px',
                  cursor: signales[a.id] ? 'default' : 'pointer',
                }}
              >
                {signales[a.id] ? '✓ Signalé' : '📣 Manque'}
              </button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
